import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  MagnifyingGlass,
  FileText,
  Strategy,
  Certificate,
  ShareNetwork,
  Scroll,
  Quotes,
  Star,
  Sparkle,
  Buildings,
} from "@phosphor-icons/react";
import {
  MaskedLines,
  MaskedLinesOnScroll,
  Reveal,
  Magnetic,
  spotlightHandler,
} from "@/components/motion/Reveal";
import Marquee from "@/components/site/Marquee";
import ParallaxFrame from "@/components/site/ParallaxFrame";
import LeadMagnet from "@/components/site/LeadMagnet";
import { trackWhatsAppClick } from "@/lib/track";
import { useSeo } from "@/lib/useSeo";

const HERO_BG =
  "https://images.unsplash.com/photo-1741356474357-72188f76e801?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxnb2xkJTIwbGluZSUyMGFydCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4ODA4Nzg4NHww&ixlib=rb-4.1.0&q=85";
const CHAPTER_IMG_SERVICES =
  "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMG1pbmltYWx8ZW58MHx8fHwxNzg4MDg3ODg1fDA&ixlib=rb-4.1.0&q=85";
const CHAPTER_IMG_TRAINING =
  "https://images.unsplash.com/photo-1498262257252-c282316270bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMG1pbmltYWx8ZW58MHx8fHwxNzg4MDg3ODg1fDA&ixlib=rb-4.1.0&q=85";
const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_ramana-learning/artifacts/v9xctajy_IMG_0035.jpeg";

const TRUST_BADGES = [
  { k: "18+ yrs", v: "Global MNC experience" },
  { k: "CPVA", v: "Certified Patent Valuation Analyst" },
  { k: "50+", v: "Granted patents as inventor" },
  { k: "10", v: "IP training programmes" },
  { k: "5★", v: "Learner feedback" },
];

const TRUSTED_BY = [
  "R&D teams of Global MNCs",
  "Patent Attorneys",
  "IIT spin-outs",
  "Deep-tech startups",
  "Pharma & MedTech innovators",
];

const HOME_TESTIMONIALS = [
  {
    quote:
      "I recently completed two insightful courses on AI-Based Patentability Search and Patent Invalidity Search. Both were well-structured, easy to follow, and provided practical insights that helped me sharpen my patent searching skills. Highly recommended.",
    name: "Aparna Gautam",
    role: "Patent Analyst, UnitedLex",
  },
  {
    quote:
      "It was a truly enriching experience learning about Patent Landscape Analysis. The topic was explained exceptionally well, supported by live case studies, and Venkataramanan sir addressed all queries with great clarity and depth.",
    name: "Dr. Arpita Banerjee",
    role: "Aspiring Patent Professional",
  },
];

const TRAINING = [
  { icon: Scroll, k: "Foundations track", d: "Introduction to IP Law & the Indian Patents Act, 1970." },
  { icon: MagnifyingGlass, k: "Search & analytics", d: "Patentability Search · Trademark Search — taught with AI-assisted workflows." },
  { icon: FileText, k: "Drafting & prosecution", d: "Patent Drafting across mechanical, pharmaceutical and other domains." },
  { icon: Strategy, k: "Strategy & valuation", d: "IP Strategy · Patent Valuation · IP Commercialisation." },
  { icon: Certificate, k: "Exam prep", d: "Indian Trademark Agent Exam · CPVA credential roadmap." },
  { icon: ShareNetwork, k: "Tech transfer", d: "From lab to market — licensing models, spin-outs, term sheets." },
];

const SERVICE_CHIPS = [
  "Patentability Search",
  "Landscape Analysis",
  "Invalidity Search",
  "FTO Study",
  "Patent Drafting",
  "Illustrations",
  "IP Strategy",
  "Valuation",
  "Trademark Search & Filing",
  "IP Agreements",
];

const COURSE_INDEX = [
  ["patent-analytics", "Patent Analytics"],
  ["patent-drafting", "Patent Drafting"],
  ["trademark-search", "Trademark Search"],
  ["ip-strategy", "IP Strategy"],
  ["patent-valuation", "Patent Valuation"],
  ["technology-transfer", "Technology Transfer"],
  ["introduction-to-ip-law", "Introduction to IP Law"],
  ["indian-trademark-agent-exam", "Indian Trademark Agent Exam"],
  ["certified-patent-valuation-analyst", "Become a CPVA"],
  ["ip-commercialisation", "IP Commercialisation"],
];

function ChapterMarker({ num, label }) {
  return (
    <div className="lg:col-span-3">
      <Reveal>
        <div className="font-serif text-[6.5rem] lg:text-[9rem] leading-[0.8] text-outline-faint select-none">
          {num}
        </div>
      </Reveal>
      <Reveal delay={0.12}>
        <div className="label-overline mt-6 lg:mt-8 flex items-center gap-3">
          <span className="h-px w-8 bg-[#C6A87C]" />
          {label}
        </div>
      </Reveal>
    </div>
  );
}

function GoldCta({ to, testid, children, onClick }) {
  const inner = (
    <>
      <span className="absolute inset-0 bg-[#E2C69D] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
      <span className="relative">{children}</span>
      <ArrowRight
        size={15}
        weight="bold"
        className="relative transition-transform duration-300 group-hover:translate-x-1"
      />
    </>
  );
  const cls =
    "group relative inline-flex items-center gap-3 h-14 px-8 bg-[#C6A87C] text-[#061324] text-xs font-semibold tracking-[0.14em] uppercase overflow-hidden";
  if (to) {
    return (
      <Link to={to} data-testid={testid} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" data-testid={testid} onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

function GhostLink({ to, testid, children, className = "" }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group inline-flex items-center gap-2 py-4 text-xs tracking-[0.14em] uppercase text-[#F5F4F0]/75 hover:text-[#C6A87C] transition-colors duration-300 ${className}`}
    >
      {children}
      <ArrowUpRight
        size={14}
        className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
      />
    </Link>
  );
}

export default function Home() {
  useSeo(
    "Patent & Trademark Services India | CPVA, 50+ Patents | Gurukul of Ramana",
    "Patentability & FTO searches, patent drafting, invalidity, landscape, valuation and trademark filing — by a Certified Patent Valuation Analyst with 18+ years' global IP experience.",
  );
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const heroFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <div data-testid="home-page" className="bg-navy">
      {/* ————— KINETIC HERO ————— */}
      <section
        ref={heroRef}
        data-testid="home-hero"
        className="relative min-h-[100svh] flex flex-col overflow-hidden bg-navy"
      >
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img
            src={HERO_BG}
            alt=""
            className="w-full h-[125%] object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-navy/70" />
        </motion.div>
        <div className="absolute inset-0 blueprint-grid-dark opacity-30 pointer-events-none" />
        <div className="absolute inset-0 noise-overlay opacity-[0.12] pointer-events-none" />

        <motion.div
          style={{ opacity: heroFade }}
          className="relative flex-1 flex flex-col justify-center w-full max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 pt-40 pb-16"
        >
          <motion.p
            data-testid="home-hero-overline"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="label-overline flex items-center gap-3"
          >
            <span className="h-px w-10 bg-[#C6A87C]" />
            Gurukul of Ramana · Bangalore · India
          </motion.p>

          <MaskedLines
            testid="home-hero-title"
            delay={0.35}
            className="mt-8 text-[8vw] sm:text-5xl lg:text-6xl xl:text-[4.75rem] leading-[1.12] tracking-tight text-[#F5F4F0] font-medium max-w-6xl"
            lines={[
              <>Patent &amp; Trademark Search,</>,
              <>Drafting and Strategy —</>,
              <>
                by a <em className="not-italic text-[#C6A87C]">CPVA with 50+ granted patents</em>
              </>,
            ]}
          />

          <motion.p
            data-testid="home-hero-subtitle"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.9 }}
            className="mt-10 text-lg md:text-xl font-light tracking-wide text-[#8A99AA] leading-relaxed max-w-2xl"
          >
            End-to-end IP support for startups, R&amp;D teams and in-house counsel —
            patentability and FTO searches, drafting, invalidity, landscape, valuation and
            trademark filing.{" "}
            <span className="text-[#F5F4F0]">18+ years of global MNC experience.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-12 flex flex-wrap items-center gap-7"
          >
            <Magnetic>
              <Link
                to="/patent-services#enquiry"
                data-testid="hero-enroll-cta"
                className="group relative inline-flex items-center gap-3 h-14 px-9 bg-[#0F9D6B] text-[#F5F4F0] text-xs font-semibold tracking-[0.16em] uppercase overflow-hidden"
              >
                <span className="absolute inset-0 bg-[#14B981] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
                <span className="relative">Book a free 15-minute review</span>
                <ArrowRight
                  size={15}
                  weight="bold"
                  className="relative transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
            </Magnetic>
            <GhostLink to="/patent-services" testid="hero-services-cta">
              Explore services
            </GhostLink>
          </motion.div>

          <motion.p
            data-testid="home-hero-trust"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.9 }}
            className="mt-10 text-sm font-light text-[#8A99AA]/80 max-w-2xl leading-relaxed border-l border-[#C6A87C]/40 pl-4"
          >
            Trusted by MNC R&amp;D teams, patent attorneys, IIT spin-outs and deep-tech
            startups across mechanical, pharma and medtech.
          </motion.p>
        </motion.div>

        {/* Stat rail */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          data-testid="trust-strip"
          className="relative border-t border-[#C6A87C]/15 bg-[#061324]/40 backdrop-blur-sm"
        >
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-8 grid grid-cols-2 md:grid-cols-5 gap-8">
            {TRUST_BADGES.map((b) => (
              <div key={b.k} data-testid={`trust-${b.k}`} className="border-l border-[#C6A87C]/40 pl-4">
                <div className="font-serif text-3xl text-[#F5F4F0] leading-none">{b.k}</div>
                <div className="label-overline mt-2 !text-[10px] text-[#8A99AA]">{b.v}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ————— TRUSTED BY ————— */}
      <section data-testid="trusted-by" className="bg-navy border-b border-[#C6A87C]/10">
        <Reveal>
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-10 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-10">
            <div className="label-overline whitespace-nowrap flex items-center gap-2">
              <Buildings size={14} weight="light" /> Trusted by
            </div>
            <div className="flex-1 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#8A99AA]">
              {TRUSTED_BY.map((t, i) => (
                <span key={t} data-testid={`trusted-by-${i}`} className="flex items-center gap-3">
                  <span className="font-medium text-[#F5F4F0]/85">{t}</span>
                  {i < TRUSTED_BY.length - 1 && (
                    <span className="w-1 h-1 bg-[#C6A87C] rounded-full" />
                  )}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ————— EDITORIAL MARQUEE ————— */}
      <Marquee
        items={[
          "Patent Analytics",
          "Trademark Search",
          "IP Strategy",
          "Patent Valuation",
          "Technology Transfer",
          "Patent Drafting",
        ]}
      />

      {/* ————— MANIFESTO CHAPTERS ————— */}
      <section data-testid="two-doors-section" className="relative bg-navy">
        {/* 01 — SERVICES */}
        <article
          data-testid="patent-services-card"
          className="relative border-b border-[#C6A87C]/10"
        >
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-24 lg:py-36 grid lg:grid-cols-12 gap-12 lg:gap-14">
            <ChapterMarker num="01" label="Practice One — Services" />
            <div className="lg:col-span-5">
              <MaskedLinesOnScroll
                className="text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.04] tracking-tight text-[#F5F4F0]"
                lines={[
                  <>Patent &amp; Trademark</>,
                  <>
                    <em className="italic text-[#C6A87C]">Services</em>
                  </>,
                ]}
              />
              <Reveal delay={0.15}>
                <p className="mt-4 font-serif text-xl text-[#C6A87C] italic">
                  From Patentability &amp; Trademark Searches to IP Strategy and more.
                </p>
                <p className="mt-6 text-[#8A99AA] font-light leading-relaxed">
                  End-to-end IP services for startups, R&amp;D teams and in-house
                  counsel — executed with the rigour of an 18-year MNC veteran.
                </p>
              </Reveal>
              <Reveal delay={0.25}>
                <div className="mt-8 flex flex-wrap gap-2">
                  {SERVICE_CHIPS.map((s) => (
                    <span
                      key={s}
                      className="px-3 py-1.5 border border-[#C6A87C]/25 text-[#F5F4F0]/65 text-[11px] uppercase tracking-[0.14em] hover:border-[#C6A87C]/70 hover:text-[#C6A87C] transition-colors duration-300"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.35}>
                <div className="mt-10 flex flex-wrap items-center gap-7">
                  <GoldCta to="/patent-services" testid="patent-services-cta">
                    Explore Services
                  </GoldCta>
                  <GhostLink to="/contact" testid="patent-services-contact">
                    Brief us on a matter
                  </GhostLink>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-4">
              <Reveal delay={0.2} className="h-full">
                <ParallaxFrame
                  src={CHAPTER_IMG_SERVICES}
                  alt="Structural concrete architecture — the discipline of claims"
                  className="aspect-[4/5] h-full min-h-[320px]"
                  caption="Structure · Claims · Freedom to operate"
                />
              </Reveal>
            </div>
          </div>
        </article>

        {/* 02 — TRAINING */}
        <article
          data-testid="ip-training-card"
          className="relative bg-navy-deep border-b border-[#C6A87C]/10 overflow-hidden"
        >
          <div className="absolute inset-0 noise-overlay opacity-[0.08] pointer-events-none" />
          <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-24 lg:py-36 grid lg:grid-cols-12 gap-12 lg:gap-14">
            <ChapterMarker num="02" label="Practice Two — Training" />
            <div className="lg:col-span-4 order-last lg:order-none">
              <Reveal delay={0.2} className="h-full">
                <ParallaxFrame
                  src={CHAPTER_IMG_TRAINING}
                  alt="Curved architectural ribs — the guru-shishya tradition, online"
                  className="aspect-[4/5] h-full min-h-[320px]"
                  caption="1:1 online · 45–60 min sessions"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <MaskedLinesOnScroll
                className="text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.04] tracking-tight text-[#F5F4F0]"
                lines={[
                  <>
                    IP <em className="italic text-[#C6A87C]">Training</em>
                  </>,
                ]}
              />
              <Reveal delay={0.15}>
                <p className="mt-4 font-serif text-xl text-[#C6A87C] italic">
                  The guru-shishya tradition, online.
                </p>
                <p className="mt-6 text-[#8A99AA] font-light leading-relaxed">
                  Ten 1:1 online programmes over Zoom — built for engineers, lawyers,
                  founders, and IP professionals stepping into, or levelling up within,
                  the world of patents, trademarks and IP strategy.
                </p>
              </Reveal>
              <div className="mt-9 grid sm:grid-cols-2 gap-x-8 gap-y-6">
                {TRAINING.map((t, i) => {
                  const Icon = t.icon;
                  return (
                    <Reveal key={i} delay={0.1 + i * 0.06} testid={`training-${i}`}>
                      <div className="flex gap-3 group">
                        <span className="w-8 h-8 flex-shrink-0 border border-[#C6A87C]/30 flex items-center justify-center text-[#C6A87C] mt-0.5 transition-colors duration-300 group-hover:bg-[#C6A87C] group-hover:text-[#061324]">
                          <Icon size={14} weight="light" />
                        </span>
                        <div>
                          <div className="text-[#F5F4F0] font-semibold text-sm tracking-wide">
                            {t.k}
                          </div>
                          <div className="text-xs text-[#8A99AA] leading-relaxed mt-1 font-light">
                            {t.d}
                          </div>
                        </div>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
              <Reveal delay={0.35}>
                <div className="mt-10 flex flex-wrap items-center gap-7">
                  <GoldCta to="/ip-training" testid="ip-training-cta">
                    Explore IP Training
                  </GoldCta>
                  <GhostLink to="/enroll" testid="ip-training-enroll">
                    Request enrolment
                  </GhostLink>
                </div>
              </Reveal>
            </div>
          </div>
        </article>
      </section>

      {/* ————— CURRICULUM INDEX ————— */}
      <section data-testid="curriculum-index" className="bg-abyss border-b border-[#C6A87C]/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-24 lg:py-32">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <Reveal>
                <div className="label-overline flex items-center gap-3">
                  <span className="h-px w-10 bg-[#C6A87C]" />
                  The Curriculum Index
                </div>
              </Reveal>
              <MaskedLinesOnScroll
                className="mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-[#F5F4F0]"
                lines={[<>Ten programmes.</>, <><em className="italic text-[#C6A87C]">One value chain.</em></>]}
              />
            </div>
            <Reveal delay={0.2}>
              <GhostLink to="/courses" testid="course-index-all">
                View all programmes
              </GhostLink>
            </Reveal>
          </div>

          <div className="border-b border-[#C6A87C]/10">
            {COURSE_INDEX.map(([slug, title], i) => (
              <Reveal key={slug} delay={Math.min(i * 0.04, 0.3)} y={18}>
                <Link
                  to={`/courses/${slug}`}
                  data-testid={`course-index-${slug}`}
                  className="group grid grid-cols-12 items-center gap-4 border-t border-[#C6A87C]/10 py-6 lg:py-7 transition-[background-color,padding] duration-500 hover:bg-[#C6A87C]/[0.05] hover:pl-4"
                >
                  <span className="col-span-2 sm:col-span-1 font-mono-tech text-xs text-[#C6A87C]/70 tracking-widest">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="col-span-8 sm:col-span-9 font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F5F4F0] group-hover:text-[#C6A87C] transition-colors duration-300 leading-tight">
                    {title}
                  </span>
                  <span className="col-span-2 flex justify-end">
                    <ArrowUpRight
                      size={20}
                      weight="light"
                      className="text-[#8A99AA] group-hover:text-[#C6A87C] transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— 03 · PATSEER RECOMMENDATION ————— */}
      <section
        data-testid="patseer-callout"
        className="relative bg-navy overflow-hidden border-b border-[#C6A87C]/10"
      >
        <div className="absolute inset-0 blueprint-grid-dark opacity-30 pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
          <ChapterMarker num="03" label="The Instrument" />
          <div className="lg:col-span-6">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#C6A87C]/40 text-[#C6A87C]">
                <Sparkle size={14} weight="fill" />
                <span className="text-[11px] uppercase tracking-[0.22em] font-semibold">
                  Tool we recommend
                </span>
              </div>
            </Reveal>
            <MaskedLinesOnScroll
              className="mt-7 text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-[#F5F4F0]"
              lines={[
                <>
                  PatSeer Premier <span className="text-[#C6A87C]">+ AI</span>
                </>,
                <>Patent Tool / Database.</>,
              ]}
            />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#8A99AA] font-light leading-relaxed max-w-xl">
                We use PatSeer Premier + AI Patent Tool / Database on all our projects
                and in our experience it is very good — with lots of features loaded in
                it. Contact us so we can provide you a referral and get you a discount
                on their pricing.
              </p>
              <p className="mt-4 text-xs text-[#8A99AA]/60 font-mono-tech uppercase tracking-[0.18em]">
                Independent recommendation from working experience.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-3 flex lg:justify-end items-start">
            <Reveal delay={0.25}>
              <a
                data-testid="patseer-referral-cta"
                href={
                  "https://wa.me/919819998036?text=" +
                  encodeURIComponent(
                    "Hi Venkataramanan, I'd like a PatSeer Premier referral / discount code. My use case: "
                  )
                }
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick("patseer_referral")}
                className="group relative inline-flex items-center gap-3 h-14 px-8 border border-[#C6A87C] text-[#C6A87C] text-xs font-semibold tracking-[0.14em] uppercase overflow-hidden transition-colors duration-500 hover:text-[#061324]"
              >
                <span className="absolute inset-0 bg-[#C6A87C] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
                <span className="relative">Request referral</span>
                <ArrowRight size={15} weight="bold" className="relative" />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ————— TESTIMONIALS ————— */}
      <section
        data-testid="home-testimonials"
        className="bg-navy-deep border-b border-[#C6A87C]/10 py-24 lg:py-32"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <Reveal>
                <div className="label-overline flex items-center gap-2">
                  <Star size={14} weight="fill" /> What our learners say
                </div>
              </Reveal>
              <MaskedLinesOnScroll
                className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-[#F5F4F0]"
                lines={[<>Real words from</>, <><em className="italic text-[#C6A87C]">real cohorts.</em></>]}
              />
            </div>
            <Reveal delay={0.2}>
              <GhostLink to="/ip-training#testimonials" testid="home-testimonials-more">
                More testimonials
              </GhostLink>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {HOME_TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={0.12 + i * 0.12} testid={`home-testimonial-${i}`}>
                <figure
                  onMouseMove={spotlightHandler}
                  className="spotlight-card h-full bg-navy border border-[#C6A87C]/15 p-8 lg:p-10 flex flex-col"
                >
                  <Quotes size={26} weight="fill" className="text-[#C6A87C]" />
                  <blockquote className="mt-6 font-serif text-xl lg:text-2xl text-[#F5F4F0] leading-snug">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-auto pt-6 border-t border-[#C6A87C]/15 mt-8">
                    <div className="text-sm font-semibold text-[#F5F4F0]">{t.name}</div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#8A99AA] mt-1.5">
                      {t.role}
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————— FOUNDER ————— */}
      <section
        data-testid="founder-strip"
        className="bg-navy border-b border-[#C6A87C]/10 py-24 lg:py-32"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-4">
            <Reveal>
              <ParallaxFrame
                src={FOUNDER_IMG}
                alt="Venkataramanan Mandakolathur — Founder"
                className="aspect-[4/5]"
                imgClassName="object-top"
                caption="Founder · IP Strategist & Prolific Inventor"
              />
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <Quotes size={38} weight="fill" className="text-[#C6A87C]" />
            </Reveal>
            <MaskedLinesOnScroll
              className="mt-6 font-serif text-3xl lg:text-5xl text-[#F5F4F0] leading-[1.15]"
              lines={[
                <>&ldquo;An invention well-claimed is</>,
                <>
                  half-defended. Well-valued,{" "}
                  <em className="italic text-[#C6A87C]">half-monetised.&rdquo;</em>
                </>,
              ]}
            />
            <Reveal delay={0.15}>
              <p className="mt-8 text-[#8A99AA] font-light leading-relaxed max-w-2xl text-lg">
                Venkataramanan Mandakolathur — 18+ years across patent analytics, IP
                strategy, and IP training. Prolific inventor on 50+ granted patents
                worldwide. Trainer to IP professionals and freshers through 1:1 online
                cohorts and the Udemy platform.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-9">
                <GhostLink to="/about" testid="founder-strip-about">
                  Read full bio
                </GhostLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ————— LEAD MAGNET ————— */}
      <LeadMagnet />

      {/* ————— FINAL CTA ————— */}
      <section
        data-testid="home-final-cta"
        className="relative bg-abyss overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid-dark opacity-30 pointer-events-none" />
        <div className="absolute inset-0 noise-overlay opacity-[0.12] pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-28 lg:py-40 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="label-overline flex items-center gap-3">
                <span className="h-px w-10 bg-[#C6A87C]" />
                Get in touch
              </div>
            </Reveal>
            <MaskedLinesOnScroll
              className="mt-6 text-4xl sm:text-5xl lg:text-7xl leading-[1.03] tracking-tight text-[#F5F4F0]"
              lines={[
                <>Patent matter, training</>,
                <>
                  enquiry, or a{" "}
                  <em className="italic text-[#C6A87C]">strategic</em>
                </>,
                <>IP question?</>,
              ]}
            />
            <Reveal delay={0.2}>
              <p className="mt-8 text-[#8A99AA] font-light max-w-xl text-lg leading-relaxed">
                Tell us a little about what you need — Venkataramanan responds within
                one business day.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col lg:items-end gap-5">
            <Reveal delay={0.25}>
              <Magnetic>
                <Link
                  to="/contact"
                  data-testid="home-cta-contact"
                  className="group relative inline-flex items-center gap-3 h-14 px-9 bg-[#C6A87C] text-[#061324] text-xs font-semibold tracking-[0.16em] uppercase overflow-hidden"
                >
                  <span className="absolute inset-0 bg-[#E2C69D] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
                  <span className="relative">Contact us</span>
                  <ArrowRight size={15} weight="bold" className="relative transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Magnetic>
            </Reveal>
            <Reveal delay={0.35}>
              <GhostLink to="/ip-training" testid="home-cta-training">
                Explore IP Training
              </GhostLink>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
